import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight, Play, Puzzle, Clock, PenLine, Rss, Sparkles, Mic,
  Check, X, Minus,
} from "lucide-react";

const TICKER_HEADLINES = [
  "AI agents quietly replace tier-1 support at three Fortune 500 firms",
  "Senate hearing reopens question of platform liability — for the fourth time",
  "Indie game developer outsells AAA launch with a 3-person team",
  "Climate insurance market sees first quarterly contraction in a decade",
  "Anthropic ships task-budget model — engineers debate the new pricing math",
  "Two-thirds of newsroom layoffs in 2026 hit data and investigative desks",
];

const STATS = [
  { k: "12", l: "Niches covered" },
  { k: "40+", l: "Trusted sources" },
  { k: "72h", l: "Freshness window" },
  { k: "<10s", l: "From story to script" },
];

const PAIN_POINTS = [
  {
    icon: Puzzle, color: "#7C3AED", bg: "#EFE6FE",
    title: "Scattered sources",
    body: "Newsletters, trend dashboards, social feeds. None of them talk to each other.",
  },
  {
    icon: Clock, color: "#EC4899", bg: "#FBE2EE",
    title: "Stale by the time you use it",
    body: "\"Trending\" doesn't mean recent. You waste time chasing spikes that already peaked.",
  },
  {
    icon: PenLine, color: "#F97316", bg: "#FFE7D2",
    title: "Data, not direction",
    body: "Most tools hand you a headline and call it a day. Turning that into a hook is still on you.",
  },
];

const SOLUTIONS = [
  {
    n: "01",
    icon: Rss, color: "#7C3AED", bg: "#EFE6FE",
    title: "Fresh, not just trending",
    body: "Everything in your feed is from the past 72 hours. If it's in Fount, it's still relevant — no digging through last month's spike to find this week's story.",
  },
  {
    n: "02",
    icon: Sparkles, color: "#EC4899", bg: "#FBE2EE",
    title: "Ready-to-record, not raw data",
    body: "Every story comes with a talking point and a hook already drafted. You're editing, not starting from a blank page.",
  },
  {
    n: "03",
    icon: Mic, color: "#F97316", bg: "#FFE7D2",
    title: "Tuned to your voice",
    body: "Fount learns how you actually talk — so the hook sounds like your show, not a press release.",
  },
];

/*
 * Comparison table rows. `values` maps to columns [TrendDash, AIChatbot, Fount].
 * "y" = yes / included, "n" = no, "p" = partial / with caveats.
 */
const COMPARE_ROWS = [
  { label: "Fresh news from the last 72 hours",     values: ["y", "n", "y"] },
  { label: "Talking points already drafted",         values: ["n", "p", "y"] },
  { label: "Hooks already drafted",                  values: ["n", "p", "y"] },
  { label: "Learns your voice over time",            values: ["n", "n", "y"] },
  { label: "Delivered on a recurring rhythm",        values: ["p", "n", "y"] },
  { label: "Zero prompt engineering",                values: ["y", "n", "y"] },
  { label: "Weekly publish calendar",                values: ["n", "n", "y"] },
  { label: "Built for creators, not analysts",       values: ["n", "n", "y"] },
];
const COMPARE_COLS = ["Trend dashboard", "AI chatbot", "Fount"];

export default function Landing() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-newsprint">
      {/* Datelined strip */}
      <div className="border-b border-[var(--border)] bg-white/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-9 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.25em] text-gray-600">
          <span>VOL. 01 · ISSUE 02</span>
          <span className="hidden md:inline">
            {new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }).toUpperCase()}
          </span>
          <span>EDITION · LIVE</span>
        </div>
      </div>

      {/* Header */}
      <header className="px-4 sm:px-6 lg:px-10 py-4 sm:py-5 flex items-center justify-between bg-white/70 backdrop-blur-sm border-b border-[var(--border)]">
        <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="landing-home">
          <span className="brand-mark"><span /><span /><span /></span>
          <span className="font-display text-[22px] sm:text-[26px] tracking-[-0.02em] font-semibold leading-none">Fount</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-gray-700">
          <Link to="/pricing" className="hover:text-black" data-testid="landing-pricing">Pricing</Link>
          <a href="#how" className="hover:text-black">How it works</a>
          <a href="#compare" className="hover:text-black">Compare</a>
          <Link to="/feedback" className="hover:text-black" data-testid="landing-feedback">Feedback</Link>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          {user ? (
            <Link to="/app" data-testid="landing-app" className="bg-black text-white hover:bg-gray-800 rounded-lg px-4 sm:px-5 py-2.5 text-sm font-medium transition-colors inline-flex items-center gap-2">
              Open app <ArrowRight size={14} strokeWidth={2} />
            </Link>
          ) : (
            <>
              <Link to="/login" data-testid="landing-login" className="hidden sm:inline text-sm text-gray-700 hover:text-black">Log in</Link>
              <Link to="/register" data-testid="landing-signup" className="bg-black text-white hover:bg-gray-800 rounded-lg px-4 sm:px-5 py-2.5 text-sm font-medium transition-colors">
                Get Started
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Breaking ticker — kept as Fount's editorial spine */}
      <div className="border-b border-[var(--border)] bg-[#0a0a0a] text-white overflow-hidden">
        <div className="flex items-center">
          <div className="shrink-0 bg-brand-gradient px-4 py-2 text-[10px] font-mono uppercase tracking-[0.3em] flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-white pulse-dot rounded-full" />
            Breaking
          </div>
          <div className="flex-1 overflow-hidden relative">
            <div className="ticker-track flex whitespace-nowrap py-2 text-xs font-mono">
              {[...TICKER_HEADLINES, ...TICKER_HEADLINES].map((h, i) => (
                <span key={`${h}-${i}`} className="px-8 inline-flex items-center gap-3">
                  <span className="text-gray-500">●</span>
                  <span className="opacity-85">{h}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* -------------------- 1. HERO -------------------- */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-24 pb-14 sm:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-6 fade-up">
          <div className="pill-badge" data-testid="hero-badge">Signal for storytellers</div>

          <h1 className="font-display text-[44px] sm:text-6xl lg:text-[80px] leading-[0.98] tracking-[-0.035em] font-medium mt-6 text-black">
            The news happens.<br />
            Your episode shouldn&apos;t take{" "}
            <span className="text-gradient italic">three hours</span>{" "}
            to catch up.
          </h1>

          <p className="text-lg lg:text-xl text-gray-700 leading-relaxed mt-7 max-w-xl">
            Fount turns the last 72 hours of headlines in your niche into
            ready-to-record talking points, hooks, and a weekly publish
            calendar — written in your voice, not a wire report&apos;s.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
            <Link
              to={user ? "/app" : "/register"}
              data-testid="landing-cta"
              className="group inline-flex items-center gap-3 bg-black text-white hover:bg-gray-900 rounded-xl px-6 py-3.5 font-medium text-sm transition-colors"
            >
              {user ? "Open Fount" : "Start your first brief"}
              <ArrowRight size={16} strokeWidth={1.8} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#how"
              data-testid="landing-secondary"
              className="inline-flex items-center gap-2 text-sm text-gray-800 hover:text-black underline underline-offset-8 decoration-[#EC4899] decoration-2 hover:decoration-[#7C3AED] transition-colors"
            >
              See it in action ↓
            </a>
          </div>
        </div>

        <div className="lg:col-span-6">
          <HeroBlades />
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-[var(--border)] bg-white/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-y-6">
          {STATS.map((s) => (
            <div key={s.l} className="text-center md:border-r last:border-r-0 border-[var(--border)]">
              <div className="font-display text-4xl lg:text-5xl tracking-[-0.03em] font-medium">
                <span className="text-gradient">{s.k}</span>
              </div>
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-gray-500 mt-2">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* -------------------- 2. THE PROBLEM -------------------- */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#EC4899] font-semibold">
              The problem
            </div>
            <h2 className="font-display text-[34px] sm:text-4xl lg:text-5xl tracking-[-0.025em] font-medium leading-[1.05] mt-4">
              You&apos;re not short on stories.<br />
              You&apos;re short on time to{" "}
              <span className="text-gradient italic">turn them into one.</span>
            </h2>
            <div className="mt-6 space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed max-w-2xl">
              <p>
                Right now, &ldquo;finding today&apos;s episode&rdquo; means five tabs open at once — a
                newsletter inbox, Google Trends, Reddit, maybe a paid trend tool — and then the real
                work: figuring out which story actually matters, and turning it into something you
                can say out loud.
              </p>
              <p>
                Most tools stop at the headline. You&apos;re left doing the hardest part yourself,
                every single time.
              </p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {PAIN_POINTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.title}
                  className="rounded-2xl border border-[var(--border)] bg-white p-6 fade-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="icon-tile" style={{ background: p.bg }}>
                    <Icon size={20} strokeWidth={1.8} color={p.color} />
                  </div>
                  <h3 className="font-display text-xl tracking-tight font-medium mt-5">{p.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mt-2">{p.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* -------------------- 3. THE SOLUTION -------------------- */}
      <section id="features" className="border-b border-[var(--border)] bg-white/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#7C3AED] font-semibold">
              The solution
            </div>
            <h2 className="font-display text-[34px] sm:text-4xl lg:text-5xl tracking-[-0.025em] font-medium leading-[1.05] mt-4">
              One feed. Twelve niches.<br />
              Talking points{" "}
              <span className="text-gradient italic">already written.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed max-w-2xl">
              Fount tracks fresh headlines across 12 niches and does the translation work most tools
              skip — turning raw news into talking points and hooks you can record from today, not
              eventually.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
            {SOLUTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.title}
                  className="rounded-2xl border border-[var(--border)] bg-white p-6 sm:p-7 hover:border-black transition-colors fade-up"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <div className="flex items-start justify-between">
                    <div className="icon-tile" style={{ background: s.bg }}>
                      <Icon size={20} strokeWidth={1.8} color={s.color} />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gray-400">{s.n}</span>
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl tracking-tight font-medium mt-5 leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mt-3">{s.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* -------------------- 4. HOW IT WORKS -------------------- */}
      <section id="how" className="border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#F97316] font-semibold">
              How it works
            </div>
            <h2 className="font-display text-[34px] sm:text-4xl lg:text-5xl tracking-[-0.025em] font-medium leading-[1.05] mt-4">
              From headline to hook in{" "}
              <span className="text-gradient italic">three steps.</span>
            </h2>
          </div>

          <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { n: "01", t: "Pick your niches", b: "Fount tracks the beats that matter to your show." },
              { n: "02", t: "Get your daily brief", b: "Fresh stories, already shaped into talking points and hooks." },
              { n: "03", t: "Record", b: "Say it in your words — the groundwork's done." },
            ].map((step, i) => (
              <li
                key={step.n}
                className="relative bg-white border border-[var(--border)] rounded-2xl p-6 sm:p-7 fade-up"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <div className="font-display text-[54px] sm:text-[64px] leading-none tracking-[-0.04em] text-gradient font-medium">
                  {step.n}
                </div>
                <div className="mt-4 font-display text-xl sm:text-2xl tracking-tight font-medium">
                  {step.t}
                </div>
                <div className="mt-2 text-sm text-gray-600 leading-relaxed">{step.b}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* -------------------- 5. DIFFERENTIATION -------------------- */}
      <section id="compare" className="border-b border-[var(--border)] bg-white/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
          <div className="max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500 font-semibold">
              Why Fount is different
            </div>
            <h2 className="font-display text-[34px] sm:text-4xl lg:text-5xl tracking-[-0.025em] font-medium leading-[1.05] mt-4">
              Not another news aggregator.<br />
              Not a{" "}
              <span className="text-gradient italic">generic AI summarizer.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed max-w-2xl">
              A trend dashboard tells you what&apos;s happening. A generic AI chatbot can summarize
              an article if you ask it to, every time, from scratch. Fount does neither — it&apos;s
              built specifically to turn news into content, on a recurring rhythm, in a voice
              that&apos;s actually yours.
            </p>
          </div>

          <ComparisonTable />
        </div>
      </section>

      {/* -------------------- 6. CLOSING CTA -------------------- */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-brand-soft pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20 sm:py-28 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-600">The last mile</div>
            <h2 className="font-display text-[36px] sm:text-5xl lg:text-6xl tracking-[-0.03em] font-medium leading-[0.98] mt-4">
              Your next episode is{" "}
              <span className="text-gradient italic">already</span> in the news.<br />
              Let&apos;s find it.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-gray-700 leading-relaxed max-w-xl">
              Start with today&apos;s brief — see what Fount would hand you before you record.
            </p>
          </div>
          <div className="md:text-right">
            <Link
              to={user ? "/app" : "/register"}
              data-testid="landing-closing-cta"
              className="inline-flex items-center gap-3 bg-black text-white hover:bg-gray-900 rounded-xl px-7 sm:px-8 py-4 font-medium text-sm transition-colors"
            >
              {user ? "Open the app" : "Get my first brief"}
              <ArrowRight size={16} strokeWidth={1.8} />
            </Link>
            <div className="mt-3 text-[11px] font-mono uppercase tracking-widest text-gray-500 md:text-right">
              No credit card required to start.
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--border)] bg-white/70 px-6 lg:px-10 py-8 flex items-center justify-between text-[11px] font-mono uppercase tracking-widest text-gray-600 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="brand-mark"><span /><span /><span /></span>
          <span>Fount Stories · Signal for storytellers</span>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <Link to="/pricing" className="hover:text-black">Pricing</Link>
          <Link to="/support" className="hover:text-black" data-testid="footer-support">Support</Link>
          <Link to="/feedback" className="hover:text-black" data-testid="footer-feedback">Feedback</Link>
          <Link to="/refund-policy" className="hover:text-black" data-testid="footer-refunds">Refunds</Link>
          <Link to="/terms" className="hover:text-black" data-testid="footer-terms">Terms</Link>
          <Link to="/privacy" className="hover:text-black" data-testid="footer-privacy">Privacy</Link>
          <Link to="/login" className="hover:text-black">Sign in</Link>
          <span className="text-gray-400">v1.0</span>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Comparison table ---------- */
function ComparisonTable() {
  return (
    <div className="mt-10 sm:mt-12" data-testid="landing-compare-table">
      <div className="rounded-2xl border border-[var(--border)] bg-white overflow-hidden shadow-[0_24px_64px_-32px_rgba(124,58,237,0.15)]">
        {/* Header row */}
        <div className="grid grid-cols-[1.4fr_repeat(3,1fr)] sm:grid-cols-[1.8fr_repeat(3,1fr)] border-b border-[var(--border)] bg-[#faf7f1]">
          <div className="px-4 sm:px-6 py-4 sm:py-5 font-mono text-[10px] uppercase tracking-[0.28em] text-gray-500 font-semibold">
            Capability
          </div>
          {COMPARE_COLS.map((c, i) => {
            const isFount = i === COMPARE_COLS.length - 1;
            return (
              <div
                key={c}
                className={
                  "px-3 sm:px-4 py-4 sm:py-5 text-center font-display text-[13px] sm:text-base tracking-tight font-semibold border-l border-[var(--border)] " +
                  (isFount ? "text-white" : "text-gray-700")
                }
                style={isFount ? {
                  backgroundImage: "linear-gradient(90deg,#7C3AED 0%,#EC4899 50%,#F97316 100%)",
                } : undefined}
              >
                {c}
              </div>
            );
          })}
        </div>

        {/* Body rows */}
        {COMPARE_ROWS.map((row, ri) => (
          <div
            key={row.label}
            className={
              "grid grid-cols-[1.4fr_repeat(3,1fr)] sm:grid-cols-[1.8fr_repeat(3,1fr)] border-b border-[var(--border)] last:border-b-0 " +
              (ri % 2 === 1 ? "bg-[#fbf9f4]" : "bg-white")
            }
          >
            <div className="px-4 sm:px-6 py-4 text-[13px] sm:text-sm text-gray-900 leading-snug font-medium">
              {row.label}
            </div>
            {row.values.map((v, ci) => (
              <div key={`${row.label}-col-${ci}`} className="px-3 sm:px-4 py-4 border-l border-[var(--border)] flex items-center justify-center">
                <CompareCell value={v} highlight={ci === row.values.length - 1} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap text-[10px] font-mono uppercase tracking-widest text-gray-500">
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-emerald-600" /> Yes</span>
          <span className="inline-flex items-center gap-1.5"><Minus size={12} className="text-amber-600" /> Partial</span>
          <span className="inline-flex items-center gap-1.5"><X size={12} className="text-rose-500" /> No</span>
        </div>
        <a
          href="/fount-comparison-table.svg"
          download
          data-testid="download-comparison-svg"
          className="hover:text-black inline-flex items-center gap-2 transition-colors"
        >
          Download comparison
          <ArrowRight size={11} strokeWidth={2} />
        </a>
      </div>
    </div>
  );
}

function CompareCell({ value, highlight }) {
  if (value === "y") {
    return (
      <span
        className={
          "inline-flex items-center justify-center w-8 h-8 rounded-full " +
          (highlight ? "bg-brand-gradient text-white" : "bg-emerald-50 text-emerald-700 border border-emerald-200")
        }
      >
        <Check size={15} strokeWidth={2.5} />
      </span>
    );
  }
  if (value === "p") {
    return (
      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
        <Minus size={15} strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-50 text-gray-400 border border-gray-200">
      <X size={14} strokeWidth={2.5} />
    </span>
  );
}

/* ---------- Hero gradient blades ---------- */
function HeroBlades() {
  return (
    <div className="relative w-full aspect-[5/4] max-w-[560px] mx-auto lg:ml-auto" aria-hidden="true">
      <svg viewBox="0 0 560 480" className="w-full h-full">
        <defs>
          {[
            { id: "g1", hex: "#7C3AED" },
            { id: "g2", hex: "#EC4899" },
            { id: "g3", hex: "#F43F5E" },
            { id: "g4", hex: "#F97316" },
          ].map((g) => (
            <linearGradient key={g.id} id={g.id} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0"    stopColor={g.hex} stopOpacity="0.0" />
              <stop offset="0.08" stopColor={g.hex} stopOpacity="0.85" />
              <stop offset="0.45" stopColor={g.hex} stopOpacity="1" />
              <stop offset="0.78" stopColor={g.hex} stopOpacity="0.65" />
              <stop offset="1"    stopColor={g.hex} stopOpacity="0" />
            </linearGradient>
          ))}
          <filter id="soft" x="-10%" y="-10%" width="120%" height="120%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
          <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="14" />
          </filter>
        </defs>

        <ellipse cx="290" cy="240" rx="220" ry="160" fill="url(#g2)" opacity="0.18" filter="url(#glow)" />

        <g className="float-1">
          <ellipse cx="320" cy="118" rx="220" ry="10" fill="#000" opacity="0.06" />
          <rect x="70" y="80" width="470" height="42" rx="21" fill="url(#g1)" filter="url(#soft)" />
        </g>
        <g className="float-2">
          <ellipse cx="320" cy="208" rx="220" ry="10" fill="#000" opacity="0.06" />
          <rect x="90" y="170" width="465" height="42" rx="21" fill="url(#g2)" filter="url(#soft)" />
        </g>
        <g className="float-3">
          <ellipse cx="320" cy="298" rx="220" ry="10" fill="#000" opacity="0.06" />
          <rect x="80" y="260" width="475" height="42" rx="21" fill="url(#g3)" filter="url(#soft)" />
        </g>
        <g className="float-4">
          <ellipse cx="320" cy="388" rx="220" ry="10" fill="#000" opacity="0.06" />
          <rect x="100" y="350" width="455" height="42" rx="21" fill="url(#g4)" filter="url(#soft)" />
        </g>
      </svg>
    </div>
  );
}
